import { Link } from 'react-router-dom';
import { ActivityFeedItem } from '../services/pusher.service';
import { ScreenshotViewer } from './ScreenshotViewer';

// Type guards for payload extraction
interface ContactObservedPayload {
  observation: {
    kind: 'NEW_CONTACT' | 'EXISTING_CONTACT';
    type: 'FOIA' | 'CRAWLER';
    observedAt: string;
    normalizedTitle: string | null;
    url: string | null;
    screenshotKey?: string | null;
  };
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    title: string | null;
    belongsTo: Array<{ institution: { name: string; state: string } }>;
  };
}

interface EmailVerifiedPayload {
  verification: {
    email: string;
    status: string;
    verifiedAt: string;
  };
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    belongsTo: Array<{ institution: { name: string; state: string } }>;
  };
}

interface ContactFieldChangedPayload {
  change: {
    contactId: string;
    field: string;
    oldValue: unknown;
    newValue: unknown;
    changedAt: string;
  };
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    belongsTo: Array<{ institution: { name: string; state: string } }>;
  };
}

function getContactName(contact: { firstName: string | null; lastName: string | null }): string {
  const parts = [contact.firstName, contact.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown';
}

function getTitle(
  contact: { title: string | null },
  observation?: { normalizedTitle: string | null }
): string | null {
  return contact.title || observation?.normalizedTitle || null;
}

function getInstitutionInfo(
  belongsTo: Array<{ institution: { name: string; state: string } }> | undefined
): string {
  if (!belongsTo || belongsTo.length === 0) return '';
  const inst = belongsTo[0].institution;
  return inst.state ? `${inst.name}, ${inst.state}` : inst.name;
}

function getDomain(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

export interface FeedDescriptionOptions {
  /**
   * Drop the contact-level facts: the name and link, and the institution.
   *
   * On a contact's own detail page every row would otherwise name that contact and link
   * back to the page the reader is already standing on. The institution goes with it -
   * it comes from the contact snapshot, not from the observation, so it renders the same
   * characters on every row and the page states it already. Each branch is rephrased
   * rather than having the words spliced out, so the result still reads as a sentence.
   */
  hideContact?: boolean;

  /**
   * Offer the crawl's screenshot, where one exists, as a camera beside the domain.
   *
   * Off by default so the feed and stream keep reading as prose. The table turns it on:
   * it replaced the page's observations list, which had this affordance, and a reader
   * checking where a title came from wants to see the page we read it off.
   */
  showScreenshot?: boolean;

  /**
   * Drop the leading emoji and the email-status glyph.
   *
   * The cards use emoji as the row's only visual marker of what kind of event it is. A
   * table already carries that in its own Type column, so there the emoji is a second
   * answer to a question the row has answered - and a ragged left edge down the
   * description column.
   */
  hideEmoji?: boolean;
}

/**
 * The prose for one feed item, shared by every surface that renders activity.
 *
 * Extracted from FeedItem so the compact table and the card cannot drift into two
 * descriptions of the same event. FeedItem still owns its own layout - only the sentence
 * lives here.
 */
export function getFeedItemDescription(
  item: ActivityFeedItem,
  options: FeedDescriptionOptions = {}
): React.ReactNode {
  const hideContact = options.hideContact ?? false;
  const hideEmoji = options.hideEmoji ?? false;
  const showScreenshot = options.showScreenshot ?? false;
  const payload = item.payload as ContactObservedPayload | EmailVerifiedPayload | null;

  if (item.eventType === 'CONTACT_OBSERVED' && payload && 'observation' in payload) {
    const p = payload as ContactObservedPayload;
    const contactName = getContactName(p.contact);
    const institution = getInstitutionInfo(p.contact.belongsTo);
    const isFoia = p.observation.type === 'FOIA';
    const title = getTitle(p.contact, p.observation);
    const domain = isFoia ? null : getDomain(p.observation.url);
    const foiaPrefix = hideEmoji ? '' : '🏷️ ';
    const crawlerPrefix = hideEmoji ? '' : '🌍 ';
    const screenshot = showScreenshot ? (
      <ScreenshotViewer screenshotKey={p.observation.screenshotKey} />
    ) : null;

    if (hideContact) {
      return isFoia ? (
        <>
          {`${foiaPrefix}Verified by the district`}
          {title ? ` as ${title}` : ''}
        </>
      ) : (
        <>
          {/* "Observed", matching the Type tag, rather than a synonym of it. */}
          {`${crawlerPrefix}Observed`}
          {title ? ` as ${title}` : ''}
          {p.observation.url && (
            <> on <span className="feed-item__mono">{domain || p.observation.url}</span></>
          )}
          {screenshot && <> {screenshot}</>}
        </>
      );
    }

    return isFoia ? (
      <>
        {`${foiaPrefix}District verified `}
        <Link to={`/directory/people/${p.contact.id}`} className="feed-item__link">{contactName}</Link>
        {title ? `, ${title}${institution ? ',' : ''}` : ''}
        {institution ? ` at ${institution}` : ''}
      </>
    ) : (
      <>
        {`${crawlerPrefix}Found `}
        <Link to={`/directory/people/${p.contact.id}`} className="feed-item__link">{contactName}</Link>
        {title ? `, ${title}${institution ? ',' : ''}` : ''}
        {institution ? ` at ${institution}` : ''}
        {p.observation.url && (
          <> on <span className="feed-item__mono">{domain || p.observation.url}</span></>
        )}
        {screenshot && <> {screenshot}</>}
      </>
    );
  }

  if (item.eventType === 'EMAIL_VERIFIED' && payload && 'verification' in payload) {
    const p = payload as EmailVerifiedPayload;
    const contactName = getContactName(p.contact);
    const email = p.verification.email;
    const status = p.verification.status;
    const statusLabel = status.toLowerCase().replace(/_/g, ' ');

    // Emoji based on status category
    let statusEmoji = '';
    if (hideEmoji) {
      statusEmoji = '';
    } else if (status === 'VALID') {
      statusEmoji = ' ✅';
    } else if (status === 'CATCH_ALL') {
      statusEmoji = ' 📬';
    } else if (['INVALID', 'ABUSE', 'SPAMTRAP', 'DO_NOT_MAIL'].includes(status)) {
      statusEmoji = ' ❌';
    }

    return (
      <>
        <span className="feed-item__mono">{email}</span>
        {` verified as ${statusLabel}${statusEmoji} email address by ZeroBounce`}
        {!hideContact && (
          <>
            {' for '}
            <Link to={`/directory/people/${p.contact.id}`} className="feed-item__link">{contactName}</Link>
          </>
        )}
      </>
    );
  }

  if (item.eventType === 'CONTACT_FIELD_CHANGED' && payload && 'change' in payload) {
    const p = payload as unknown as ContactFieldChangedPayload;
    const contactName = getContactName(p.contact);
    const field = p.change.field;
    const oldValue = p.change.oldValue;
    const newValue = p.change.newValue;

    // Format field name for display
    const fieldLabel = field.replace(/_/g, ' ');

    // Format values for display
    const formatValue = (val: unknown): string => {
      if (val === null || val === undefined) return 'empty';
      if (typeof val === 'string') return val || 'empty';
      return String(val);
    };

    if (hideContact) {
      // Sentence-initial without a name in front of it, so the field leads.
      const leadingLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
      return (
        <>
          {`${leadingLabel} changed from `}
          <span className="feed-item__mono">{formatValue(oldValue)}</span>
          {' to '}
          <span className="feed-item__mono">{formatValue(newValue)}</span>
        </>
      );
    }

    return (
      <>
        <Link to={`/directory/people/${p.contact.id}`} className="feed-item__link">{contactName}</Link>
        {`'s ${fieldLabel} changed from `}
        <span className="feed-item__mono">{formatValue(oldValue)}</span>
        {' to '}
        <span className="feed-item__mono">{formatValue(newValue)}</span>
      </>
    );
  }

  return 'Activity event';
}
